import {Formik} from "formik";
import {
    Alert,
    Button,
    Col,
    Container,
    Form,
    Modal,
    Row,
    Spinner,
    Tab,
    Tabs
} from "react-bootstrap";
import styles from "../licensemanagement.module.css";
import {useNavigate} from "react-router";
import * as yup from "yup";
import {useContext, useState} from "react";
import {BackendContext} from "../../../context/Backend";
import {useTranslation} from "react-i18next";
import {Clipboard, ClipboardCheck} from "react-bootstrap-icons";

const TYPE_PRODUCTION = "production";
const TYPE_TEST = "test";
const MAX_COUNT = 100;

export const NewForm = (props) => {

    const navigate = useNavigate();
    const backend = useContext(BackendContext);
    const {t} = useTranslation();
    const [createdLicenses, setCreatedLicenses] = useState(null); // array of base64 strings
    const [createdInfo, setCreatedInfo] = useState(null);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [copiedAll, setCopiedAll] = useState(false);

    const handleCancel = () => {
        navigate("/");
    }

    const handleCopyOne = (idx) => {
        if (createdLicenses && createdLicenses[idx] != null) {
            navigator.clipboard.writeText(createdLicenses[idx]);
            setCopiedIndex(idx);
            setTimeout(() => setCopiedIndex(null), 2000);
        }
    }

    const handleCopyAll = () => {
        if (createdLicenses && createdLicenses.length > 0) {
            navigator.clipboard.writeText(createdLicenses.join("\n"));
            setCopiedAll(true);
            setTimeout(() => setCopiedAll(false), 2000);
        }
    }

    const handleCloseModal = () => {
        setCreatedLicenses(null);
        setCreatedInfo(null);
        setCopiedIndex(null);
        setCopiedAll(false);
    }

    const schema = yup.object().shape({
        tabBasic: yup.object().shape({
            type: yup.string().oneOf([TYPE_PRODUCTION, TYPE_TEST]).required(),
            email: yup.string().when("type", {
                is: TYPE_PRODUCTION,
                then: (s) => s
                    .email()
                    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Invalid email format')
                    .required(),
                otherwise: (s) => s.notRequired()
            }),
            expiry: yup.string().when("type", {
                is: TYPE_PRODUCTION,
                then: (s) => s.required(),
                otherwise: (s) => s.notRequired()
            }),
            validity: yup.number().when("type", {
                is: TYPE_TEST,
                then: (s) => s.integer().positive().required(),
                otherwise: (s) => s.notRequired()
            }),
            count: yup.number().when("type", {
                is: TYPE_TEST,
                then: (s) => s.integer().min(1).max(MAX_COUNT).required(),
                otherwise: (s) => s.notRequired()
            })
        })
    });

    return (<>
        <Formik
            validationSchema={schema}
            initialValues={{
                tabBasic: {
                    type: TYPE_PRODUCTION,
                    email: '',
                    expiry: '',
                    validity: 30,
                    count: 1
                }
            }}
            onSubmit={(values, actions) => {
                const isTest = values.tabBasic.type === TYPE_TEST;
                const payload = isTest
                    ? {
                        type: TYPE_TEST,
                        validity: Number(values.tabBasic.validity),
                        count: Number(values.tabBasic.count)
                    }
                    : {
                        type: TYPE_PRODUCTION,
                        email: values.tabBasic.email,
                        expiry: values.tabBasic.expiry
                    };
                backend.api.createLicense(payload, (resp) => {
                    setCreatedLicenses(resp && resp.licenses ? resp.licenses : []);
                    setCreatedInfo({...values.tabBasic});
                    actions.resetForm();
                }, (error) => {
                    navigate("/error", backend.api.getErrorState(error));
                });
            }}>
            {({handleSubmit, handleChange, errors, values, isSubmitting, dirty, isValid}) => (
                <Form className={"p-0 " + styles.tabs} onSubmit={handleSubmit} autoComplete={"off"}>
                    <Row>
                        <Tabs
                            defaultActiveKey="basic"
                            transition={true}
                            id="edit-tab"
                            className={"mb-0 bg-primary"}
                        >
                            <Tab eventKey="basic" title={t('license.header')}
                                 tabClassName={!!errors.tabBasic ? ("text-danger") : null}>
                                <Container fluid={true} className={"py-4"}>
                                    <Row>
                                        <Col>
                                            <div className={"inputContainer"}>
                                                <Form.Label htmlFor="tabBasic.type"
                                                            className={styles.label}>{t('license.type')}</Form.Label>
                                                <Form.Select
                                                    name={"tabBasic.type"}
                                                    value={values.tabBasic.type}
                                                    onChange={handleChange}>
                                                    <option value={TYPE_PRODUCTION}>{t('license.production')}</option>
                                                    <option value={TYPE_TEST}>{t('license.test')}</option>
                                                </Form.Select>
                                            </div>
                                        </Col>
                                    </Row>
                                    {values.tabBasic.type === TYPE_PRODUCTION && (
                                        <>
                                            <Row>
                                                <Col>
                                                    <div className={"inputContainer"}>
                                                        <Form.Label htmlFor="tabBasic.email"
                                                                    className={styles.label}>{t('profile.email')}</Form.Label>
                                                        <Form.Control type="text" name={"tabBasic.email"}
                                                                      onChange={handleChange}
                                                                      value={values.tabBasic.email}
                                                                      isInvalid={!!errors.tabBasic?.email}/>
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <div className={"inputContainer"}>
                                                        <Form.Label htmlFor="tabBasic.expiry"
                                                                    className={styles.label}>{t('license.expiry')}</Form.Label>
                                                        <Form.Control type="date" name={"tabBasic.expiry"}
                                                                      onChange={handleChange}
                                                                      value={values.tabBasic.expiry}
                                                                      isInvalid={!!errors.tabBasic?.expiry}/>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </>
                                    )}
                                    {values.tabBasic.type === TYPE_TEST && (
                                        <>
                                            <Row>
                                                <Col>
                                                    <div className={"inputContainer"}>
                                                        <Form.Label htmlFor="tabBasic.validity"
                                                                    className={styles.label}>{t('license.validity')}</Form.Label>
                                                        <Form.Control type="number" min={1}
                                                                      name={"tabBasic.validity"}
                                                                      onChange={handleChange}
                                                                      value={values.tabBasic.validity}
                                                                      isInvalid={!!errors.tabBasic?.validity}/>
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <div className={"inputContainer"}>
                                                        <Form.Label htmlFor="tabBasic.count"
                                                                    className={styles.label}>{t('license.count')}</Form.Label>
                                                        <Form.Control type="number" min={1} max={MAX_COUNT}
                                                                      name={"tabBasic.count"}
                                                                      onChange={handleChange}
                                                                      value={values.tabBasic.count}
                                                                      isInvalid={!!errors.tabBasic?.count}/>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </>
                                    )}
                                </Container>
                            </Tab>
                        </Tabs>
                    </Row>
                    <Row>
                        <div className="d-flex gap-2 p-2 justify-content-center">
                            <Button className={"websobutton"} size={"sm"} variant={"danger"}
                                    onClick={handleCancel}>{t('system.cancelButton')}</Button>
                            <Button className={"websobutton"} type={"submit"}
                                    disabled={isSubmitting || !dirty || !isValid}
                                    size={"md"}>
                                {isSubmitting ? (<Spinner
                                    className={"me-2"}
                                    as="span"
                                    animation="border"
                                    size="sm"
                                />) : null}{t('system.saveButton')}</Button>
                        </div>
                    </Row>
                </Form>
            )}</Formik>

        <Modal show={!!createdLicenses} onHide={handleCloseModal} centered size={createdLicenses && createdLicenses.length > 1 ? "lg" : undefined}>
            <Modal.Header closeButton>
                <Modal.Title>{t('license.created')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {createdInfo && (
                    <div className="mb-3">
                        <p><strong>{t('license.type')}:</strong> {createdInfo.type === TYPE_TEST ? t('license.test') : t('license.production')}</p>
                        {createdInfo.type === TYPE_PRODUCTION && (
                            <>
                                <p><strong>{t('profile.email')}:</strong> {createdInfo.email}</p>
                                <p><strong>{t('license.expiry')}:</strong> {createdInfo.expiry}</p>
                            </>
                        )}
                        {createdInfo.type === TYPE_TEST && (
                            <>
                                <p><strong>{t('license.validity')}:</strong> {createdInfo.validity}</p>
                                <p><strong>{t('license.count')}:</strong> {createdInfo.count}</p>
                            </>
                        )}
                    </div>
                )}
                {createdLicenses && createdLicenses.length > 1 && (
                    <div className="mb-2 d-flex justify-content-end">
                        <Button variant="outline-secondary" size="sm" onClick={handleCopyAll}>
                            {copiedAll ? <ClipboardCheck/> : <Clipboard/>} {t('license.copyAll')}
                        </Button>
                    </div>
                )}
                {createdLicenses && createdLicenses.map((lic, idx) => (
                    <Alert key={idx} variant="success" className="d-flex align-items-center justify-content-between">
                        <code className="flex-grow-1 text-break">{lic}</code>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleCopyOne(idx)}
                            className="ms-2"
                        >
                            {copiedIndex === idx ? <ClipboardCheck/> : <Clipboard/>}
                        </Button>
                    </Alert>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={handleCloseModal}>
                    {t('system.closeButton')}
                </Button>
            </Modal.Footer>
        </Modal></>
    );
};
